import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Title,
  Group,
  Pagination,
  LoadingOverlay,
  Card,
  Text,
  Stack,
  Modal,
  Grid,
  Badge,
  Divider,
  Button,
  Tabs,
  Table,
  ActionIcon,
  Tooltip,
  ScrollArea,
  TextInput,
  Select,
} from '@mantine/core';
import { IconHistory, IconFileExcel, IconRefresh, IconDatabase, IconCalendar, IconDownload, IconSearch, IconX } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { storicoService } from '../../lib/storico-service';
import { storicoPersonalizzatiService, StoricoProgettoPersonalizzati, StoricoAllegatoPersonalizzati } from '../../services/storico-personalizzati-service';
import { StoricoTable } from '../../components/storico/StoricoTable';
import { StoricoPersonalizzatiTable } from '../../components/storico/StoricoPersonalizzatiTable';
import { StoricoFilters } from '../../components/storico/StoricoFilters';
import { PageSizeSelector } from '../../components/ui/PageSizeSelector';
import { Storico, StoricoFilter } from '../../types/storico-types';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { useDisclosure } from '@mantine/hooks';

export function StoricoPage() {
  const [activeTab, setActiveTab] = useState<string | null>('ufficio-stile');
  const [filters, setFilters] = useState<StoricoFilter>({
    pageNumber: 1,
    pageSize: 20,
  });
  
  // Filtri per Storico Progetti
  const [searchStorico, setSearchStorico] = useState('');
  const [filterTipologia, setFilterTipologia] = useState<string | null>(null);
  const [filterStato, setFilterStato] = useState<string | null>(null);
  const [filterCliente, setFilterCliente] = useState<string | null>(null);
  const [pageStorico, setPageStorico] = useState(1);
  const [pageSizeStorico, setPageSizeStorico] = useState(20);

  // Filtri per Storico 2019
  const [searchStorico2019, setSearchStorico2019] = useState('');
  const [filterTipologia2019, setFilterTipologia2019] = useState<string | null>(null);
  const [filterStato2019, setFilterStato2019] = useState<string | null>(null);
  const [filterCliente2019, setFilterCliente2019] = useState<string | null>(null);
  const [pageStorico2019, setPageStorico2019] = useState(1);
  const [pageSizeStorico2019, setPageSizeStorico2019] = useState(20);
  
  const [selectedStorico, setSelectedStorico] = useState<Storico | null>(null);
  const [selectedPersonalizzato, setSelectedPersonalizzato] = useState<StoricoProgettoPersonalizzati | null>(null);
  const [detailOpened, { open: openDetail, close: closeDetail }] = useDisclosure(false);
  
  // Query per ottenere i dati da Personalizzati
  const { data: personalizzatiData, isLoading: personalizzatiLoading, error: personalizzatiError } = useQuery({
    queryKey: ['storico-personalizzati'],
    queryFn: () => storicoPersonalizzatiService.getAllStorici(),
  });

  // Filtra e pagina i dati di Storico Progetti
  const filteredStoricoProgetti = useMemo(() => {
    if (!personalizzatiData?.storicoProgetti) return { items: [], total: 0, totalPages: 0 };
    
    const filtered = personalizzatiData.storicoProgetti.filter((progetto) => {
      // Filtro ricerca testuale
      if (searchStorico && !progetto.titoloProgetto?.toLowerCase().includes(searchStorico.toLowerCase()) &&
          !progetto.idProgressivo?.toLowerCase().includes(searchStorico.toLowerCase())) {
        return false;
      }
      
      // Filtro tipologia
      if (filterTipologia && progetto.tipologiaProgetto !== filterTipologia) {
        return false;
      }
      
      // Filtro stato
      if (filterStato && progetto.statoFlusso !== filterStato) {
        return false;
      }
      
      // Filtro cliente
      if (filterCliente && !progetto.topClient?.includes(filterCliente) && !progetto.codiceCliente?.includes(filterCliente)) {
        return false;
      }
      
      return true;
    });
    
    const total = filtered.length;
    const totalPages = Math.ceil(total / pageSizeStorico);
    const startIndex = (pageStorico - 1) * pageSizeStorico;
    const items = filtered.slice(startIndex, startIndex + pageSizeStorico);
    
    return { items, total, totalPages };
  }, [personalizzatiData?.storicoProgetti, searchStorico, filterTipologia, filterStato, filterCliente, pageStorico, pageSizeStorico]);

  // Filtra e pagina i dati di Storico 2019
  const filteredStorico2019 = useMemo(() => {
    if (!personalizzatiData?.storico2019Progetti) return { items: [], total: 0, totalPages: 0 };
    
    const filtered = personalizzatiData.storico2019Progetti.filter((progetto) => {
      // Filtro ricerca testuale
      if (searchStorico2019 && !progetto.titoloProgetto?.toLowerCase().includes(searchStorico2019.toLowerCase()) &&
          !progetto.idProgressivo?.toLowerCase().includes(searchStorico2019.toLowerCase())) {
        return false;
      }
      
      // Filtro tipologia
      if (filterTipologia2019 && progetto.tipologiaProgetto !== filterTipologia2019) {
        return false;
      }
      
      // Filtro stato
      if (filterStato2019 && progetto.statoFlusso !== filterStato2019) {
        return false;
      }
      
      // Filtro cliente
      if (filterCliente2019 && !progetto.topClient?.includes(filterCliente2019) && !progetto.codiceCliente?.includes(filterCliente2019)) {
        return false;
      }
      
      return true;
    });
    
    const total = filtered.length;
    const totalPages = Math.ceil(total / pageSizeStorico2019);
    const startIndex = (pageStorico2019 - 1) * pageSizeStorico2019;
    const items = filtered.slice(startIndex, startIndex + pageSizeStorico2019);
    
    return { items, total, totalPages };
  }, [personalizzatiData?.storico2019Progetti, searchStorico2019, filterTipologia2019, filterStato2019, filterCliente2019, pageStorico2019, pageSizeStorico2019]);

  // Opzioni per i filtri (estrae valori unici)
  const tipologieStorico = useMemo(() => {
    if (!personalizzatiData?.storicoProgetti) return [];
    const unique = [...new Set(personalizzatiData.storicoProgetti.map(p => p.tipologiaProgetto).filter(Boolean))];
    return unique.sort();
  }, [personalizzatiData?.storicoProgetti]);

  const statiStorico = useMemo(() => {
    if (!personalizzatiData?.storicoProgetti) return [];
    const unique = [...new Set(personalizzatiData.storicoProgetti.map(p => p.statoFlusso).filter(Boolean))];
    return unique.sort();
  }, [personalizzatiData?.storicoProgetti]);

  const clientiStorico = useMemo(() => {
    if (!personalizzatiData?.storicoProgetti) return [];
    const unique = [...new Set(personalizzatiData.storicoProgetti.map(p => p.topClient || p.codiceCliente).filter(Boolean))];
    return unique.sort();
  }, [personalizzatiData?.storicoProgetti]);

  const tipologieStorico2019 = useMemo(() => {
    if (!personalizzatiData?.storico2019Progetti) return [];
    const unique = [...new Set(personalizzatiData.storico2019Progetti.map(p => p.tipologiaProgetto).filter(Boolean))];
    return unique.sort();
  }, [personalizzatiData?.storico2019Progetti]);

  const statiStorico2019 = useMemo(() => {
    if (!personalizzatiData?.storico2019Progetti) return [];
    const unique = [...new Set(personalizzatiData.storico2019Progetti.map(p => p.statoFlusso).filter(Boolean))];
    return unique.sort();
  }, [personalizzatiData?.storico2019Progetti]);

  const clientiStorico2019 = useMemo(() => {
    if (!personalizzatiData?.storico2019Progetti) return [];
    const unique = [...new Set(personalizzatiData.storico2019Progetti.map(p => p.topClient || p.codiceCliente).filter(Boolean))];
    return unique.sort();
  }, [personalizzatiData?.storico2019Progetti]);

  // Query per ottenere i dati filtrati di Ufficio Stile
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['storico', filters],
    queryFn: () => storicoService.getFiltered(filters),
  });

  // Debug logging
  useEffect(() => {
    if (personalizzatiError) {
      console.error('❌ Personalizzati Error:', personalizzatiError);
    }
  }, [personalizzatiData, personalizzatiError]);

  // Query per ottenere le categorie (per i filtri)
  const { data: categorie = [] } = useQuery({
    queryKey: ['storico-categorie'],
    queryFn: () => storicoService.getCategorie(),
  });

  // Query per ottenere gli stati progetto (per i filtri)
  const { data: statiProgetto = [] } = useQuery({
    queryKey: ['storico-stati'],
    queryFn: () => storicoService.getStatiProgetto(),
  });

  // Query per ottenere le tipologie distinte (per il Select Tipologia)
  const { data: tipologieStorico_BE = [] } = useQuery({
    queryKey: ['storico-tipologie'],
    queryFn: () => storicoService.getTipologie(),
  });

  // Query per ottenere gli anni distinti (per il Select Anno)
  const { data: anniStorico_BE = [] } = useQuery({
    queryKey: ['storico-anni'],
    queryFn: () => storicoService.getAnni(),
  });

  const handleFiltersChange = (newFilters: StoricoFilter) => {
    setFilters({
      ...newFilters,
      pageNumber: 1, // Reset alla prima pagina quando cambiano i filtri
      pageSize: filters.pageSize,
    });
  };

  const handleSearch = () => {
    refetch();
  };

  const handleReset = () => {
    setFilters({
      pageNumber: 1,
      pageSize: 20,
    });
  };

  const handlePageChange = (page: number) => {
    setFilters({
      ...filters,
      pageNumber: page,
    });
  };

  const handleRowClick = (storico: Storico) => {
    setSelectedStorico(storico);
    setSelectedPersonalizzato(null);
    openDetail();
  };

  const handlePersonalizzatoRowClick = (progetto: StoricoProgettoPersonalizzati) => {
    setSelectedPersonalizzato(progetto);
    setSelectedStorico(null);
    openDetail();
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy', { locale: it });
    } catch {
      return '-';
    }
  };

  const handleDownloadAllegato = async (allegato: StoricoAllegatoPersonalizzati) => {
    try {
      // Chiama l'endpoint per ottenere l'URL con SAS token
      const result = await storicoPersonalizzatiService.getStoricoProgettoAllegatoDownloadUrl(allegato.id);
      
      if (result?.url) {
        // Apri l'URL con SAS token in una nuova tab
        window.open(result.url, '_blank');
      } else {
        console.error('URL non disponibile per il download');
      }
    } catch (error) {
      console.error('Errore durante il download dell\'allegato:', error);
      // Fallback: prova con l'URL diretto se disponibile
      if (allegato.url) {
        window.open(allegato.url, '_blank');
      }
    }
  };

  if (error) {
    return (
      <Container size="xl" py="xl">
        <Card withBorder p="xl" mt="md">
          <Text c="red" ta="center">
            Si è verificato un errore nel caricamento dei dati storici.
          </Text>
          <Group justify="center" mt="md">
            <Button onClick={() => refetch()} leftSection={<IconRefresh size={16} />}>
              Riprova
            </Button>
          </Group>
        </Card>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between">
          <Group>
            <IconHistory size={32} />
            <Title order={2}>Storico Progetti</Title>
          </Group>
        </Group>

        {/* Tabs per separare Ufficio Stile e Personalizzati */}
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="ufficio-stile" leftSection={<IconDatabase size={16} />}>
              Ufficio Stile
              {data?.totalCount && <Badge ml="xs" size="sm" variant="light">{data.totalCount}</Badge>}
            </Tabs.Tab>
            <Tabs.Tab value="personalizzati" leftSection={<IconFileExcel size={16} />}>
              Storico Progetti
              <Badge ml="xs" size="sm" variant="light">{filteredStoricoProgetti.total}</Badge>
            </Tabs.Tab>
            <Tabs.Tab value="personalizzati-2019" leftSection={<IconCalendar size={16} />}>
              Storico 2019
              <Badge ml="xs" size="sm" variant="light">{filteredStorico2019.total}</Badge>
              
            </Tabs.Tab>
          </Tabs.List>

          {/* Tab Ufficio Stile */}
          <Tabs.Panel value="ufficio-stile" pt="md">
            <Stack gap="md">
              {/* Filtri */}
              <StoricoFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onSearch={handleSearch}
                onReset={handleReset}
                categorie={categorie}
                statiProgetto={statiProgetto}
                tipologie={tipologieStorico_BE}
                anni={anniStorico_BE}
                loading={isLoading}
              />

              {/* Tabella con loading overlay */}
              <Card withBorder p={0} style={{ position: 'relative' }}>
                <LoadingOverlay visible={isLoading} />
                
                {data?.items && (
                  <>
                    <StoricoTable
                      data={data.items}
                      onRowClick={handleRowClick}
                    />
                    
                    {/* Paginazione */}
                    <Group justify="space-between" p="md">
                      <Group gap="lg">
                        <Text size="sm" c="dimmed">
                          Pagina {data.pageNumber} di {data.totalPages}
                          {' '}({data.items.length} di {data.totalCount} record)
                        </Text>
                        <PageSizeSelector
                          value={filters.pageSize ?? 20}
                          onChange={(size) => setFilters({ ...filters, pageSize: size, pageNumber: 1 })}
                        />
                      </Group>
                      {data.totalPages > 1 && (
                        <Pagination
                          total={data.totalPages}
                          value={data.pageNumber}
                          onChange={handlePageChange}
                        />
                      )}
                    </Group>
                  </>
                )}

                {!isLoading && (!data?.items || data.items.length === 0) && (
                  <Card.Section p="xl">
                    <Text ta="center" c="dimmed">
                      Nessun dato trovato. Modifica i filtri di ricerca.
                    </Text>
                  </Card.Section>
                )}
              </Card>
            </Stack>
          </Tabs.Panel>

          {/* Tab Storico Progetti Personalizzati */}
          <Tabs.Panel value="personalizzati" pt="md">
            <Stack gap="md">
              {/* Filtri */}
              <Card withBorder p="md">
                <Grid>
                  <Grid.Col span={3}>
                    <TextInput
                      placeholder="Cerca per titolo o ID..."
                      leftSection={<IconSearch size={16} />}
                      value={searchStorico}
                      onChange={(e) => setSearchStorico(e.currentTarget.value)}
                      rightSection={
                        searchStorico && (
                          <ActionIcon size="xs" onClick={() => setSearchStorico('')}>
                            <IconX size={14} />
                          </ActionIcon>
                        )
                      }
                    />
                  </Grid.Col>
                  <Grid.Col span={3}>
                    <Select
                      placeholder="Filtra per tipologia"
                      data={tipologieStorico}
                      value={filterTipologia}
                      onChange={setFilterTipologia}
                      clearable
                    />
                  </Grid.Col>
                  <Grid.Col span={3}>
                    <Select
                      placeholder="Filtra per stato"
                      data={statiStorico}
                      value={filterStato}
                      onChange={setFilterStato}
                      clearable
                    />
                  </Grid.Col>
                  <Grid.Col span={3}>
                    <Select
                      placeholder="Filtra per cliente"
                      data={clientiStorico}
                      value={filterCliente}
                      onChange={setFilterCliente}
                      clearable
                      searchable
                    />
                  </Grid.Col>
                </Grid>
              </Card>

              <Card withBorder p={0} style={{ position: 'relative' }}>
                <LoadingOverlay visible={personalizzatiLoading} />
                {filteredStoricoProgetti.items.length > 0 ? (
                  <StoricoPersonalizzatiTable
                    data={filteredStoricoProgetti.items}
                    onRowClick={handlePersonalizzatoRowClick}
                  />
                ) : (
                  <Card.Section p="xl">
                    <Text ta="center" c="dimmed">
                      {searchStorico || filterTipologia || filterStato || filterCliente
                        ? 'Nessun progetto trovato con i filtri selezionati.'
                        : 'Nessun progetto storico disponibile.'}
                    </Text>
                  </Card.Section>
                )}
              </Card>
              
              {/* Paginazione */}
              <Group justify="space-between" mt="md">
                <Group gap="lg">
                  <Text size="sm" c="dimmed">
                    Visualizzati {Math.min((pageStorico - 1) * pageSizeStorico + 1, filteredStoricoProgetti.total)}-{Math.min(pageStorico * pageSizeStorico, filteredStoricoProgetti.total)} di {filteredStoricoProgetti.total}
                  </Text>
                  <PageSizeSelector
                    value={pageSizeStorico}
                    onChange={(size) => {
                      setPageSizeStorico(size);
                      setPageStorico(1);
                    }}
                  />
                </Group>
                {filteredStoricoProgetti.totalPages > 1 && (
                  <Pagination
                    value={pageStorico}
                    onChange={(page) => {
                      setPageStorico(page);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    total={filteredStoricoProgetti.totalPages}
                  />
                )}
              </Group>
            </Stack>
          </Tabs.Panel>

          {/* Tab Storico 2019 Personalizzati */}
          <Tabs.Panel value="personalizzati-2019" pt="md">
            <Stack gap="md">
              {/* Filtri */}
              <Card withBorder p="md">
                <Grid>
                  <Grid.Col span={3}>
                    <TextInput
                      placeholder="Cerca per titolo o ID..."
                      leftSection={<IconSearch size={16} />}
                      value={searchStorico2019}
                      onChange={(e) => setSearchStorico2019(e.currentTarget.value)}
                      rightSection={
                        searchStorico2019 && (
                          <ActionIcon size="xs" onClick={() => setSearchStorico2019('')}>
                            <IconX size={14} />
                          </ActionIcon>
                        )
                      }
                    />
                  </Grid.Col>
                  <Grid.Col span={3}>
                    <Select
                      placeholder="Filtra per tipologia"
                      data={tipologieStorico2019}
                      value={filterTipologia2019}
                      onChange={setFilterTipologia2019}
                      clearable
                    />
                  </Grid.Col>
                  <Grid.Col span={3}>
                    <Select
                      placeholder="Filtra per stato"
                      data={statiStorico2019}
                      value={filterStato2019}
                      onChange={setFilterStato2019}
                      clearable
                    />
                  </Grid.Col>
                  <Grid.Col span={3}>
                    <Select
                      placeholder="Filtra per cliente"
                      data={clientiStorico2019}
                      value={filterCliente2019}
                      onChange={setFilterCliente2019}
                      clearable
                      searchable
                    />
                  </Grid.Col>
                </Grid>
              </Card>

              <Card withBorder p={0} style={{ position: 'relative' }}>
                <LoadingOverlay visible={personalizzatiLoading} />
                {filteredStorico2019.items.length > 0 ? (
                  <StoricoPersonalizzatiTable
                    data={filteredStorico2019.items}
                    onRowClick={handlePersonalizzatoRowClick}
                  />
                ) : (
                  <Card.Section p="xl">
                    <Text ta="center" c="dimmed">
                      {searchStorico2019 || filterTipologia2019 || filterStato2019 || filterCliente2019
                        ? 'Nessun progetto trovato con i filtri selezionati.'
                        : 'Nessun progetto storico 2019 disponibile.'}
                    </Text>
                  </Card.Section>
                )}
              </Card>
              
              {/* Paginazione */}
              <Group justify="space-between" mt="md">
                <Group gap="lg">
                  <Text size="sm" c="dimmed">
                    Visualizzati {Math.min((pageStorico2019 - 1) * pageSizeStorico2019 + 1, filteredStorico2019.total)}-{Math.min(pageStorico2019 * pageSizeStorico2019, filteredStorico2019.total)} di {filteredStorico2019.total}
                  </Text>
                  <PageSizeSelector
                    value={pageSizeStorico2019}
                    onChange={(size) => {
                      setPageSizeStorico2019(size);
                      setPageStorico2019(1);
                    }}
                  />
                </Group>
                {filteredStorico2019.totalPages > 1 && (
                  <Pagination
                    value={pageStorico2019}
                    onChange={(page) => {
                      setPageStorico2019(page);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    total={filteredStorico2019.totalPages}
                  />
                )}
              </Group>
            </Stack>
          </Tabs.Panel>
        </Tabs>
      </Stack>

      {/* Modal dettaglio */}
      <Modal
        opened={detailOpened}
        onClose={closeDetail}
        title={selectedPersonalizzato ? "Dettaglio Progetto Personalizzato" : "Dettaglio Progetto Ufficio Stile"}
        size="xl"
      >
        <ScrollArea h={600}>
          {selectedStorico && (() => {
            const storico = selectedStorico;
            return (
            <Stack gap="md">
              <Grid>
                <Grid.Col span={6}>
                  <Text size="sm" fw={500}>Categoria</Text>
                  <Text size="sm" c="dimmed">{storico.categoria || '-'}</Text>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Text size="sm" fw={500}>Numero Decoro</Text>
                  <Text size="sm" c="dimmed">{storico.numDecoro || '-'}</Text>
                </Grid.Col>
                <Grid.Col span={12}>
                  <Text size="sm" fw={500}>Nome Decoro</Text>
                  <Text size="sm">{storico.nomeDecoro || '-'}</Text>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Text size="sm" fw={500}>Forma Bianco</Text>
                  <Text size="sm" c="dimmed">{storico.formaBianco || '-'}</Text>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Text size="sm" fw={500}>Stato Progetto</Text>
                  {storico.statoProgetto ? (
                    <Badge style={{ color: 'white' }}>{storico.statoProgetto}</Badge>
                  ) : (
                    <Text size="sm" c="dimmed">-</Text>
                  )}
                </Grid.Col>
              </Grid>

              <Divider />

              <Grid>
                <Grid.Col span={6}>
                  <Text size="sm" fw={500}>Ordine Produzione</Text>
                  <Text size="sm" c="dimmed">{storico.ordineProduzioneNumero || '-'}</Text>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Text size="sm" fw={500}>Elaborazione</Text>
                  <Text size="sm" c="dimmed">{storico.elaborazione || '-'}</Text>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Text size="sm" fw={500}>Fine Sviluppo</Text>
                  <Text size="sm" c="dimmed">{formatDate(storico.fineSviluppoImpiantiPellicole)}</Text>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Text size="sm" fw={500}>Data Completamento</Text>
                  <Text size="sm" c="dimmed">{formatDate(storico.dataCompletamento)}</Text>
                </Grid.Col>
              </Grid>
            </Stack>
          )})()}
          
          {selectedPersonalizzato && (() => {
            const personalizzato = selectedPersonalizzato;
            return (
            <Stack gap="md">
              {/* Sezione Principale */}
              <Card withBorder p="md">
                <Text size="sm" fw={700} mb="md">Informazioni Generali</Text>
                <Grid>
                  <Grid.Col span={12}>
                    <Text size="sm" fw={500}>Titolo Progetto</Text>
                    <Text size="sm">{personalizzato.titoloProgetto || '-'}</Text>
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <Text size="sm" fw={500}>ID Progressivo</Text>
                    <Text size="sm" c="dimmed">{personalizzato.idProgressivo || '-'}</Text>
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <Text size="sm" fw={500}>Tipologia</Text>
                    <Text size="sm" c="dimmed">{personalizzato.tipologiaProgetto || '-'}</Text>
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <Text size="sm" fw={500}>Num Decoro CSPD</Text>
                    <Text size="sm" c="dimmed">{personalizzato.num_Decoro_CSPD || '-'}</Text>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Text size="sm" fw={500}>Stato Flusso</Text>
                    {personalizzato.statoFlusso ? (
                      <Badge style={{ color: 'white' }}>{personalizzato.statoFlusso}</Badge>
                    ) : (
                      <Text size="sm" c="dimmed">-</Text>
                    )}
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Text size="sm" fw={500}>Task Status</Text>
                    {personalizzato.taskStatus ? (
                      <Badge color="blue" style={{ color: 'white' }}>{personalizzato.taskStatus}</Badge>
                    ) : (
                      <Text size="sm" c="dimmed">-</Text>
                    )}
                  </Grid.Col>
                </Grid>
              </Card>

              {/* Sezione Cliente */}
              <Card withBorder p="md">
                <Text size="sm" fw={700} mb="md">Informazioni Cliente</Text>
                <Grid>
                  <Grid.Col span={6}>
                    <Text size="sm" fw={500}>Top Client</Text>
                    <Text size="sm" c="dimmed">{personalizzato.topClient || '-'}</Text>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Text size="sm" fw={500}>Codice Cliente</Text>
                    <Text size="sm" c="dimmed">{personalizzato.codiceCliente || '-'}</Text>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Text size="sm" fw={500}>Area Commerciale</Text>
                    <Text size="sm" c="dimmed">{personalizzato.areaCommerciale || '-'}</Text>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Text size="sm" fw={500}>Area Manager</Text>
                    <Text size="sm" c="dimmed">{personalizzato.areaManager || '-'}</Text>
                  </Grid.Col>
                  <Grid.Col span={12}>
                    <Text size="sm" fw={500}>Informazioni Cliente Progetto</Text>
                    <Text size="sm" c="dimmed">{personalizzato.clienteProgettoInfo || '-'}</Text>
                  </Grid.Col>
                </Grid>
              </Card>

              {/* Sezione Richiesta */}
              <Card withBorder p="md">
                <Text size="sm" fw={700} mb="md">Dettagli Richiesta</Text>
                <Grid>
                  <Grid.Col span={6}>
                    <Text size="sm" fw={500}>Richiesta</Text>
                    <Text size="sm" c="dimmed">{personalizzato.richiesta || '-'}</Text>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Text size="sm" fw={500}>Tipologia Richiesta</Text>
                    <Text size="sm" c="dimmed">{personalizzato.richiesta_TipologiaRichiesta || '-'}</Text>
                  </Grid.Col>
                  <Grid.Col span={12}>
                    <Text size="sm" fw={500}>Descrizione Richiesta</Text>
                    <Text size="sm" c="dimmed">{personalizzato.richiesta_Descrizione || '-'}</Text>
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <Text size="sm" fw={500}>Priorità PJ</Text>
                    <Text size="sm" c="dimmed">{personalizzato.priorita_Pj || '-'}</Text>
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <Text size="sm" fw={500}>Priorità Direzione</Text>
                    <Text size="sm" c="dimmed">{personalizzato.prioritaDirezione || '-'}</Text>
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <Text size="sm" fw={500}>Richiesta Costing</Text>
                    <Text size="sm" c="dimmed">{personalizzato.richiestaCosting || '-'}</Text>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Text size="sm" fw={500}>Numero GG Richiesti</Text>
                    <Text size="sm" c="dimmed">{personalizzato.richiesta_NumGG || '-'}</Text>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Text size="sm" fw={500}>Numero GG Produzione</Text>
                    <Text size="sm" c="dimmed">{personalizzato.numGGProdRich || '-'}</Text>
                  </Grid.Col>
                </Grid>
              </Card>

              {/* Sezione Date */}
              <Card withBorder p="md">
                <Text size="sm" fw={700} mb="md">Date Importanti</Text>
                <Grid>
                  <Grid.Col span={6}>
                    <Text size="sm" fw={500}>Data Invio Richiesta</Text>
                    <Text size="sm" c="dimmed">{formatDate(personalizzato.dataInvioRichiesta)}</Text>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Text size="sm" fw={500}>Data Consegna</Text>
                    <Text size="sm" c="dimmed">{formatDate(personalizzato.dataConsegna)}</Text>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Text size="sm" fw={500}>Data Richiesta Consegna</Text>
                    <Text size="sm" c="dimmed">{formatDate(personalizzato.dataRichiestaConsegna)}</Text>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Text size="sm" fw={500}>Data Effettiva</Text>
                    <Text size="sm" c="dimmed">{formatDate(personalizzato.dataEffettiva)}</Text>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Text size="sm" fw={500}>Data Stimata</Text>
                    <Text size="sm" c="dimmed">{formatDate(personalizzato.dataStimata)}</Text>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Text size="sm" fw={500}>Data Ricezione Materiale</Text>
                    <Text size="sm" c="dimmed">{formatDate(personalizzato.dataRicezioneMaterialeSupporto)}</Text>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Text size="sm" fw={500}>Data Creazione</Text>
                    <Text size="sm" c="dimmed">{formatDate(personalizzato.created)}</Text>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Text size="sm" fw={500}>Data Modifica</Text>
                    <Text size="sm" c="dimmed">{formatDate(personalizzato.modified)}</Text>
                  </Grid.Col>
                </Grid>
              </Card>

              {/* Sezione Commerciale */}
              <Card withBorder p="md">
                <Text size="sm" fw={700} mb="md">Informazioni Commerciali</Text>
                <Grid>
                  <Grid.Col span={4}>
                    <Text size="sm" fw={500}>Fatturato Stimato</Text>
                    <Text size="sm" c="dimmed">{personalizzato.fatturatoStimato ? `€ ${personalizzato.fatturatoStimato.toFixed(2)}` : '-'}</Text>
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <Text size="sm" fw={500}>Numero Articoli</Text>
                    <Text size="sm" c="dimmed">{personalizzato.numeroArticoli || '-'}</Text>
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <Text size="sm" fw={500}>Previsione Vendita PZ</Text>
                    <Text size="sm" c="dimmed">{personalizzato.previsioneVenditaPZ || '-'}</Text>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Text size="sm" fw={500}>OdV</Text>
                    <Text size="sm" c="dimmed">{personalizzato.odV || '-'}</Text>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Text size="sm" fw={500}>OdV Cliente</Text>
                    <Text size="sm" c="dimmed">{personalizzato.odVCliente || '-'}</Text>
                  </Grid.Col>
                  <Grid.Col span={12}>
                    <Text size="sm" fw={500}>Assortimento SL</Text>
                    <Text size="sm" c="dimmed">{personalizzato.assortimentoSL || '-'}</Text>
                  </Grid.Col>
                </Grid>
              </Card>

              {/* Sezione Produzione */}
              <Card withBorder p="md">
                <Text size="sm" fw={700} mb="md">Informazioni Produzione</Text>
                <Grid>
                  <Grid.Col span={6}>
                    <Text size="sm" fw={500}>Ingaggio BU</Text>
                    <Text size="sm" c="dimmed">{personalizzato.ingaggioBU || '-'}</Text>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Text size="sm" fw={500}>Tipo BU</Text>
                    <Text size="sm" c="dimmed">{personalizzato.ingaggioTipoBU || '-'}</Text>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Text size="sm" fw={500}>Tipo BU Quorum</Text>
                    <Text size="sm" c="dimmed">{personalizzato.ingaggioTipoBUquorum || '-'}</Text>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Text size="sm" fw={500}>In Attesa</Text>
                    <Text size="sm" c="dimmed">{personalizzato.inAttesa || '-'}</Text>
                  </Grid.Col>
                  <Grid.Col span={12}>
                    <Text size="sm" fw={500}>Revisionato</Text>
                    <Text size="sm" c="dimmed">{personalizzato.revisionato ? 'Sì' : 'No'}</Text>
                  </Grid.Col>
                  <Grid.Col span={12}>
                    <Text size="sm" fw={500}>Note Richiesta Modifica</Text>
                    <Text size="sm" c="dimmed">{personalizzato.noteRichiestaModifica || '-'}</Text>
                  </Grid.Col>
                  <Grid.Col span={12}>
                    <Text size="sm" fw={500}>Top Client Note</Text>
                    <Text size="sm" c="dimmed">{personalizzato.topClientNote || '-'}</Text>
                  </Grid.Col>
                </Grid>
              </Card>

              {/* Sezione Allegati con Download */}
              {personalizzato.attachments && personalizzato.attachments.length > 0 && (
                <Card withBorder p="md">
                  <Text size="sm" fw={700} mb="md">Allegati ({personalizzato.attachments.length})</Text>
                  <Stack gap="xs">
                    {personalizzato.attachments.map((att: StoricoAllegatoPersonalizzati) => (
                      <Group key={att.id} justify="space-between" p="xs" style={{ borderRadius: 4, border: '1px solid #e0e0e0' }}>
                        <Group gap="xs">
                          <IconFileExcel size={20} color="#217346" />
                          <div>
                            <Text size="sm" fw={500}>{att.fileName}</Text>
                            {att.importedDate && (
                              <Text size="xs" c="dimmed">Importato: {formatDate(att.importedDate)}</Text>
                            )}
                          </div>
                        </Group>
                        <Tooltip label="Scarica allegato">
                          <ActionIcon 
                            variant="light" 
                            color="blue"
                            onClick={() => handleDownloadAllegato(att)}
                          >
                            <IconDownload size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    ))}
                  </Stack>
                </Card>
              )}

              {/* Sezione Metadata */}
              <Card withBorder p="md">
                <Text size="sm" fw={700} mb="md">Metadati</Text>
                <Grid>
                  <Grid.Col span={6}>
                    <Text size="sm" fw={500}>Creato da</Text>
                    <Text size="sm" c="dimmed">{personalizzato.createdBy || '-'}</Text>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Text size="sm" fw={500}>Modificato da</Text>
                    <Text size="sm" c="dimmed">{personalizzato.modifiedBy || '-'}</Text>
                  </Grid.Col>
                  <Grid.Col span={12}>
                    <Text size="sm" fw={500}>Path</Text>
                    <Text size="sm" c="dimmed" style={{ wordBreak: 'break-all' }}>{personalizzato.path || '-'}</Text>
                  </Grid.Col>
                </Grid>
              </Card>
            </Stack>
          )})()}
        </ScrollArea>
      </Modal>
    </Container>
  );
}
